import Box from '@mui/material/Box'
import {
  RiAlertLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiDeleteBinLine,
  RiEditBoxLine,
  RiErrorWarningLine,
  RiEyeLine,
  RiEyeOffLine,
  RiInformationLine,
  RiPencilFill,
} from 'react-icons/ri'

import pauseDialogIcon from '@/assets/campaigns/launch/icon-pause-dialog.svg'
import playDialogIcon from '@/assets/campaigns/launch/icon-play-large.svg'
import stopDialogIcon from '@/assets/campaigns/launch/icon-stop.svg'

/** Dev Handoff 4418:9942 (negative), 4418:9966 (positive), 4418:10043 (alert). */
export type ConfirmationIconVariant =
  | 'positive'
  | 'negative'
  | 'alert'
  | 'caution'
  | 'delete'
  | 'issue'
  | 'info'
  | 'pause'
  | 'play'
  | 'stop'
  | 'visibilityOff'
  | 'visibility'
  | 'edit'
  | 'liveEdit'

const ICON_WELL: Record<
  ConfirmationIconVariant,
  | { kind: 'remix'; bgcolor: string; color: string; Icon: typeof RiAlertLine; size: number }
  | { kind: 'asset'; bgcolor: string; src: string; size: number }
> = {
  positive: { kind: 'remix', bgcolor: '#f0fdf4', color: '#16a34a', Icon: RiCheckboxCircleLine, size: 31 },
  negative: { kind: 'remix', bgcolor: '#fff0f0', color: '#ef4444', Icon: RiCloseCircleLine, size: 24 },
  alert: { kind: 'remix', bgcolor: '#fff0f0', color: '#ef4444', Icon: RiAlertLine, size: 31 },
  issue: { kind: 'remix', bgcolor: '#fff0f0', color: '#ef4444', Icon: RiAlertLine, size: 31 },
  caution: { kind: 'remix', bgcolor: '#fffbeb', color: '#f59e0b', Icon: RiErrorWarningLine, size: 28 },
  delete: { kind: 'remix', bgcolor: '#fff0f0', color: '#ef4444', Icon: RiDeleteBinLine, size: 28 },
  info: { kind: 'remix', bgcolor: '#f1faff', color: '#008cd0', Icon: RiInformationLine, size: 24 },
  /** Figma 6103:11313 — amber bars on cream circle, not red circle-outline pause. */
  pause: { kind: 'asset', bgcolor: '#f7e7cb', src: pauseDialogIcon, size: 20 },
  /** Same cream + amber treatment as pause modal (reactivate confirmation). */
  play: { kind: 'asset', bgcolor: '#f7e7cb', src: playDialogIcon, size: 20 },
  /** Figma stop CTA — red square on pink circle (not delete/bin). */
  stop: { kind: 'asset', bgcolor: '#fff0f0', src: stopDialogIcon, size: 28 },
  visibilityOff: { kind: 'remix', bgcolor: '#e0f1fe', color: '#008cd0', Icon: RiEyeOffLine, size: 24 },
  visibility: { kind: 'remix', bgcolor: '#e0f1fe', color: '#008cd0', Icon: RiEyeLine, size: 24 },
  edit: { kind: 'remix', bgcolor: '#f1faff', color: '#008cd0', Icon: RiEditBoxLine, size: 24 },
  /** BFP-1142 Figma 219:2415 — pencil on light-blue well (edit live survey). */
  liveEdit: { kind: 'remix', bgcolor: '#c7ebf9', color: '#008cd0', Icon: RiPencilFill, size: 20 },
}

type Props = Readonly<{
  variant: ConfirmationIconVariant
  testId?: string
}>

export function ConfirmationModalIcon({ variant, testId }: Props) {
  const config = ICON_WELL[variant]

  return (
    <Box
      data-testid={testId ?? `confirmation-icon-${variant}`}
      sx={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        bgcolor: config.bgcolor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: config.kind === 'remix' ? config.color : undefined,
      }}
    >
      {config.kind === 'asset' ? (
        <Box
          component="img"
          src={config.src}
          alt=""
          aria-hidden
          sx={{
            width: config.size,
            height: config.size,
            display: 'block',
            objectFit: 'contain',
          }}
        />
      ) : (
        <config.Icon size={config.size} aria-hidden />
      )}
    </Box>
  )
}
